import { useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import type { MeResponse } from '@platform/shared';
import { csrfToken } from '../../api/client';
import { intlWithNamespace, useLocale } from '../../i18n';

/** The SPA's route patterns (App.tsx), so a self-report carries the pattern rather than the URL. */
const PATTERNS = ['/', '/decisions/:id', '/sessions/:id', '/status/:id', '/threads', '/threads/:id', '/you', '/plan', '/plan/changes/:id', '/tracks', '/tracks/:id', '/tracks/:id/:tab', '/packages/:id', '/build', '/build/findings', '/build/runs/:id', '/plan/calendar', '/plan/gates', '/plan/gates/:phase', '/plan/gates/:phase/items/:itemId', '/findings/:id', '/report/authorize'];

/** The web build's SHA when it is one (`abc1234-dev` and `unknown` are not). */
function webSha(): string | null {
  const sha = __APP_SHA__.replace(/-dev$/, '');
  return /^[0-9a-f]{7,40}$/.test(sha) ? sha : null;
}

export function routePattern(pathname: string): string {
  for (const p of PATTERNS) if (matchPath({ path: p, end: true }, pathname)) return p;
  return pathname;
}

/**
 * Mounts `@coderpad/platform-report-widget` in the planning app itself (planning-app.md §3.5 "mounted
 * in the planning app itself"): `target = planning-app`, the person's session instead of a report
 * token, the web build's SHA, and the route pattern on screen. Loaded lazily after sign-in — the
 * widget, its labels and their catalog chunk (`report.widget.*`, src/pages/namespaces.ts) together —
 * so the shell's first paint carries none of it (§4.5).
 */
export function useReportWidget(me: MeResponse | null | undefined): void {
  const intl = useIntl();
  const { locale } = useLocale();
  const location = useLocation();
  const routeRef = useRef(routePattern(location.pathname));
  routeRef.current = routePattern(location.pathname);
  const subjectRef = useRef<string | undefined>(undefined);
  subjectRef.current = /^\/(threads|decisions|findings)\/([^/]+)/.exec(location.pathname)?.[0]?.slice(1);

  useEffect(() => {
    // Under Playwright the floating button would sit over what other specs click; findings.spec opts in through sessionStorage.
    if (!me || typeof window === 'undefined' || (navigator.webdriver === true && sessionStorage.getItem('platform-report-widget') !== 'on')) return;
    let cancelled = false;
    let tabbar: ResizeObserver | undefined;
    void Promise.all([import('@coderpad/platform-report-widget'), import('./widgetLabels'), intlWithNamespace(intl, locale, 'widget')]).then(([{ mountReportWidget }, { widgetLabels }, widgetIntl]) => {
      if (cancelled) return;
      const el = mountReportWidget({
        planningUrl: window.location.origin,
        target: 'planning-app',
        auth: 'session',
        csrfToken,
        route: () => routeRef.current,
        subject: () => subjectRef.current,
        shas: webSha() ? { 'planning-web': webSha()! } : undefined,
        role: () => me.person.role,
        warnings: false,
        labels: widgetLabels(widgetIntl),
      });
      // The phone's floating button stands above the tab bar (brain-ds.css §6, `--brw-bottom`), whose height is not
      // a constant — a wrapped label (*Espacio de trabajo* at 320px) makes it taller than its 56px rows — so the bar's
      // measured height is published on the host as `--tabbar-h`. A hidden bar (the desktop, the AI Assist sheet
      // over the phone) keeps the last measure: the rule only applies where the bar shows.
      const bar = document.querySelector<HTMLElement>('.tabbar');
      if (bar) {
        tabbar = new ResizeObserver(() => {
          if (bar.offsetHeight > 0) el.style.setProperty('--tabbar-h', `${bar.offsetHeight}px`);
        });
        tabbar.observe(bar);
      }
    });
    return () => {
      cancelled = true;
      tabbar?.disconnect();
    };
  }, [me, intl, locale]);
}
